import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2, Edit2, X, Package } from 'lucide-react'
import { getParts, createPart, updatePart, deletePart } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { ModalOverlay } from './components/StaffAnimations'

function PartModal({ part, profile, onClose, onSave }) {
    const isEdit = !!part?.id
    const [form, setForm] = useState({
        name: part?.name || '',
        sku: part?.sku || '',
        category: part?.category || 'Other',
        quantity: part?.quantity || 0,
        unit: part?.unit || 'pcs',
        cost_price: part?.cost_price || 0,
        dealer_price: part?.dealer_price || 0,
        sell_price: part?.sell_price || 0,
        supplier: part?.supplier || '',
        low_stock_threshold: part?.low_stock_threshold ?? 10,
        description: part?.description || '',
    })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Part' : 'Add Part'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault(); if (!form.name) { toast.error('Name required'); return }
                    onSave({
                        ...form,
                        quantity: parseInt(form.quantity) || 0,
                        low_stock_threshold: parseInt(form.low_stock_threshold) || 0,
                        cost_price: parseFloat(form.cost_price) || 0,
                        dealer_price: parseFloat(form.dealer_price) || 0,
                        sell_price: parseFloat(form.sell_price) || 0,
                        added_by: profile?.name || '',
                        added_by_id: profile?.id || '',
                    })
                }}
                    className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Part Name *</label>
                            <input value={form.name} onChange={e => set('name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">SKU</label>
                            <input value={form.sku} onChange={e => set('sku', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" placeholder="e.g. BP-001" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Category</label>
                            <input value={form.category} onChange={e => set('category', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Unit</label>
                            <select value={form.unit} onChange={e => set('unit', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                {['pcs', 'units', 'kg', 'litre', 'set', 'pair'].map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Quantity</label>
                            <input type="number" min="0" value={form.quantity} onChange={e => set('quantity', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="col-span-2 grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Purchase Price (₹)</label>
                                <input type="number" min="0" step="0.01" value={form.cost_price} onChange={e => set('cost_price', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Dealer Price (₹)</label>
                                <input type="number" min="0" step="0.01" value={form.dealer_price} onChange={e => set('dealer_price', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Customer Price (₹)</label>
                                <input type="number" min="0" step="0.01" value={form.sell_price} onChange={e => set('sell_price', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Supplier</label>
                            <input value={form.supplier} onChange={e => set('supplier', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Low Stock Alert At</label>
                            <input type="number" min="0" value={form.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Description</label>
                            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                        </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition">{isEdit ? 'Update' : 'Add Part'}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default function Inventory() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [search, setSearch] = useState('')
    const [modal, setModal] = useState(null)

    const { data: parts = [], isLoading } = useQuery({ queryKey: ['parts', search], queryFn: () => getParts(search) })

    const createMut = useMutation({ mutationFn: createPart, onSuccess: () => { qc.invalidateQueries(['parts']); setModal(null); toast.success('Part added') }, onError: e => toast.error(e.message) })
    const updateMut = useMutation({ mutationFn: ({ id, ...r }) => updatePart(id, r), onSuccess: () => { qc.invalidateQueries(['parts']); setModal(null); toast.success('Updated') }, onError: e => toast.error(e.message) })
    const deleteMut = useMutation({ mutationFn: deletePart, onSuccess: () => { qc.invalidateQueries(['parts']); toast.success('Deleted') }, onError: e => toast.error(e.message) })

    const totalValue = parts.reduce((s, i) => s + (i.quantity * parseFloat(i.cost_price || 0)), 0)
    const lowStock = parts.filter(i => i.quantity <= (i.low_stock_threshold ?? 2)).length

    return (
        <StaffLayout>
            <div className="space-y-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-white">Parts Inventory</h1>
                        <p className="text-slate-400 text-xs mt-0.5">{parts.length} items · ₹{(totalValue / 100000).toFixed(2)}L stock value{lowStock > 0 ? ` · ${lowStock} low stock` : ''}</p>
                    </div>
                    <button onClick={() => setModal('new')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                        <Plus size={16} /> Add Part
                    </button>
                </div>

                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts..."
                        className="w-full max-w-sm bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>

                <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                    {isLoading ? <div className="p-8 text-center text-slate-400">Loading...</div> :
                        parts.length === 0 ? <div className="p-12 text-center text-slate-500">No parts found</div> : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="border-b border-white/5">
                                        {['Name', 'Category', 'Qty', 'Purchase', 'Dealer', 'Customer', 'Supplier', ''].map(h => (
                                            <th key={h} className="text-left text-xs font-medium text-slate-400 px-4 py-3">{h}</th>
                                        ))}
                                    </tr></thead>
                                    <tbody className="divide-y divide-white/5">
                                        {parts.map(item => {
                                            const thresh = item.low_stock_threshold ?? 2
                                            return (
                                                <tr key={item.id} className="hover:bg-white/2 transition">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-7 h-7 rounded-lg bg-purple-600/20 flex items-center justify-center"><Package size={13} className="text-purple-400" /></div>
                                                            <div>
                                                                <span className="font-medium text-white">{item.name}</span>
                                                                {item.sku && <div className="text-xs text-slate-500">SKU: {item.sku}</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3"><span className="bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded-lg">{item.category || 'Other'}</span></td>
                                                    <td className="px-4 py-3">
                                                        <span className={`font-medium ${item.quantity <= 0 ? 'text-red-400' : item.quantity <= thresh ? 'text-amber-400' : 'text-green-400'}`}>
                                                            {item.quantity} {item.unit}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-300">₹{parseFloat(item.cost_price || 0).toLocaleString('en-IN')}</td>
                                                    <td className="px-4 py-3 text-slate-300">₹{parseFloat(item.dealer_price || 0).toLocaleString('en-IN')}</td>
                                                    <td className="px-4 py-3 text-slate-300">₹{parseFloat(item.sell_price || 0).toLocaleString('en-IN')}</td>
                                                    <td className="px-4 py-3 text-slate-400">{item.supplier || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-2">
                                                            <button onClick={() => setModal(item)} className="text-slate-400 hover:text-blue-400 transition"><Edit2 size={14} /></button>
                                                            <button onClick={() => { if (window.confirm('Delete part?')) deleteMut.mutate(item.id) }} className="text-slate-400 hover:text-red-400 transition"><Trash2 size={14} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </div>
            <ModalOverlay show={!!modal}>
                {modal && <PartModal part={modal === 'new' ? null : modal} profile={profile} onClose={() => setModal(null)} onSave={data => modal === 'new' ? createMut.mutate(data) : updateMut.mutate({ id: modal.id, ...data })} />}
            </ModalOverlay>
        </StaffLayout>
    )
}